// src/components/ui/Button.jsx
import { forwardRef } from "react";
import { Link } from "react-router-dom";

const Button = forwardRef(({
    children,
    as: Component = "button",
    variant = "solid", // solid | outline | secondary | ghost | danger
    size = "md",      // sm | md | lg | xl
    className = "",
    isLoading = false,
    leftIcon,
    rightIcon,
    ...props
}, ref) => {

    const baseStyles = "inline-flex items-center justify-center font-black uppercase tracking-widest transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed outline-none select-none";

    const variants = {
        solid:
            "bg-brand-700 text-white hover:bg-brand-800 active:bg-brand-900 " +
            "focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 border border-transparent shadow-lg shadow-brand-900/10",
        outline:
            "bg-white border-2 border-brand-700 text-brand-700 hover:bg-brand-50 active:bg-brand-100 " +
            "focus-visible:ring-2 focus-visible:ring-brand-500 shadow-sm",
        secondary:
            "bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-950 " +
            "focus-visible:ring-2 focus-visible:ring-slate-500 shadow-lg shadow-slate-900/10",
        ghost:
            "bg-transparent text-brand-700 hover:bg-brand-50 active:bg-brand-100 border-transparent",
        danger:
            "bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-lg shadow-red-600/10"
    };

    const sizes = {
        sm: "px-4 py-2 text-[10px] rounded-xl gap-2",
        md: "px-6 py-3.5 text-xs rounded-2xl gap-2.5",
        lg: "px-8 py-4.5 text-sm rounded-[1.25rem] gap-3",
        xl: "px-10 py-5.5 text-base rounded-[1.5rem] gap-4"
    };

    const finalStyles = `${baseStyles} ${variants[variant] || variants.solid} ${sizes[size] || sizes.md} ${className}`;

    // Link Handling
    const isExternal = typeof props.to === "string" && (props.to.startsWith("http") || props.to.startsWith("tel:") || props.to.startsWith("mailto:"));

    if (Component === Link && isExternal) {
        return (
            <a href={props.to} className={finalStyles} ref={ref} {...props}>
                {leftIcon}
                {isLoading ? <span className="animate-pulse">Lädt...</span> : children}
                {rightIcon}
            </a>
        );
    }

    return (
        <Component className={finalStyles} ref={ref} {...props}>
            {leftIcon}
            {isLoading ? (
                <div className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-current" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {children}
                </div>
            ) : children}
            {rightIcon}
        </Component>
    );
});

export default Button;
